import { Core } from "@faire/design-tokens";
import {
  CANT_FIND_OPTION_ID,
  StoreSearchDropdown,
} from "@faire/retailer-visitor-shared/components/StoreSearchDropdown/StoreSearchDropdown";
import { getOsName } from "@faire/retailer-visitor-shared/lib/user-agent/osName";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { OSType } from "@faire/retailer-visitor-shared/services/MobileAppBanner";
import { useSignUpEventTracker } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { StoreInfoData } from "@faire/retailer-visitor-shared/views/SignUp/store-info/__internal__/StoreInfoForm";
import { stringifyMatchBusiness } from "@faire/retailer-visitor-shared/views/SignUp/store-info/__internal__/storeInfoUtil";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web--source/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Close } from "@faire/web--source/slate/icons/foundation/Close";
import { Pin } from "@faire/web--source/slate/icons/foundation/Pin";
import { Search } from "@faire/web--source/slate/icons/foundation/Search";
import { Input } from "@faire/web--source/slate/Input";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import ClickOutside from "@faire/web--source/ui/ClickOutside";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { QueryParameters } from "@faire/web-api--source/endpoints/www/api/retailer/find-businesses/get";
import { useQuery as useFindBusinessesQuery } from "@faire/web-api--source/endpoints/www/api/retailer/find-businesses/get-hooks";
import { IMatchedBusiness } from "@faire/web-api--source/indigofair/data/IMatchedBusiness";
import { useFormikContext } from "formik";
import React, { useEffect, useRef } from "react";
import styled, { css } from "styled-components";

interface IProps {
  storeNameOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelected: (matchedBusiness?: IMatchedBusiness) => void;
}

/**
 * @deprecated Use FormikAddressSearch instead @faire/retailer-visitor-shared/src/components/FormikAddressSearch/FormikAddressSearch.tsx
 */
export const PhysicalStoreSearchInput: React.FC<IProps> = ({
  storeNameOnChange,
  onSelected,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    values,
    getFieldProps,
    touched,
    errors,
    setFieldValue,
    initialValues,
  } = useFormikContext<StoreInfoData>();

  const { isMobile } = useViewport();
  const [_isMobileOverlayOpen, setIsMobileOverlayOpen] = React.useState(false);
  const [hasSelectedStore, setHasSelectedStore] = React.useState(
    !!(
      initialValues.matched_business?.name &&
      initialValues.matched_business?.type
    )
  );
  const { trackFieldValue } = useSignUpEventTracker(Step.STORE_INFO);
  const [debounceQuery, setDebounceQuery] = React.useState("");

  const { data } = useFindBusinessesQuery(
    QueryParameters.build({
      query: debounceQuery,
      country: getSessionCountry() || THREE_LETTER_PRIMARY_COUNTRY_CODES.USA,
    })
  );
  const handleDebounce = debounceIfPossible(
    (query: string) => setDebounceQuery(query),
    200
  );
  const [showDropdown, setShowDropdown] = React.useState(false);
  const isMobileOverlayOpen = isMobile && _isMobileOverlayOpen;

  const inputRef = useRef<HTMLInputElement>(null);
  const osName = getOsName();

  // Fix for mWeb iOS bug where store name input field scrolled out of view when keyboard opened
  // due to the input field being focused before the css updated to make it fixed.
  useEffect(() => {
    if (isMobileOverlayOpen && inputRef.current && osName === OSType.IOS) {
      inputRef.current.blur();
    }
  }, [isMobileOverlayOpen, osName]);

  useEffect(() => {
    handleDebounce(values.store_name ?? "");
    return () => {
      if ("cancel" in handleDebounce) {
        handleDebounce.cancel();
      }
    };
  }, [handleDebounce, values.store_name]);

  useEffect(() => {
    if (hasSelectedStore) {
      setShowDropdown(false);
    }
  }, [hasSelectedStore]);
  const hasStoreNameError = touched.store_name && errors.store_name;

  const onDropdownSelect = (
    matchedBusiness?: IMatchedBusiness,
    storeName?: string
  ) => {
    trackFieldValue(
      "matched_business",
      stringifyMatchBusiness(matchedBusiness)
    );
    setFieldValue("matched_business", matchedBusiness);
    setFieldValue("store_name", storeName);
    setHasSelectedStore(true);
    onSelected(matchedBusiness);
  };

  return (
    <>
      {hasSelectedStore ? (
        <SelectedInput
          clear={() => {
            trackFieldValue("store_name", "");
            setFieldValue("matched_business", {});
            setFieldValue("store_name", "");
            onSelected(undefined);
            setHasSelectedStore(false);
          }}
        />
      ) : (
        <SearchInputMobileOverlay
          onClickOutside={() => {
            if (values.store_name && !values.matched_business?.type) {
              trackFieldValue("store_name", values.store_name);
              setFieldValue(
                "matched_business",
                IMatchedBusiness.build({
                  id: CANT_FIND_OPTION_ID,
                  name: values.store_name,
                  type: IMatchedBusiness.Type.MANUAL,
                })
              );
            }
            setShowDropdown(false);
          }}
          $isOpen={isMobileOverlayOpen}
        >
          <Row>
            <Spacer width={isMobileOverlayOpen ? "2x" : "0"} />
            <StyledInput
              ref={inputRef}
              data-test-id="store-name-input"
              type="text"
              label={
                isMobileOverlayOpen
                  ? undefined
                  : strictLocalize({
                      defaultMessage: "Store name",
                      description: { text: "Store name input label" },
                    })
              }
              placeholder={strictLocalize({
                defaultMessage: "Search for your store",
                description: {
                  text: "Store name input placeholder",
                  img: "https://cdn.faire.com/fastly/976f25ec0777d71a99324d3a701f6822db4d655ed97db773a5ba9b28d5350f57.png",
                },
              })}
              prefixNode={
                <SearchWrapper>
                  <Search color={Core.icon.default} />
                </SearchWrapper>
              }
              suffixNode={
                isMobileOverlayOpen ? (
                  <CloseIconContainer
                    onClick={() => {
                      trackFieldValue("matched_business", "");
                      setFieldValue("store_name", "");
                      setFieldValue("matched_business", {});
                      setHasSelectedStore(false);
                    }}
                  >
                    <Close width="12px" color={Core.icon.subdued} />
                  </CloseIconContainer>
                ) : null
              }
              {...getFieldProps("store_name")}
              onFocus={() => {
                setShowDropdown(true);
                setIsMobileOverlayOpen(true);
              }}
              error={!isMobileOverlayOpen && hasStoreNameError}
              $isRound={isMobileOverlayOpen}
              onChange={storeNameOnChange}
            />

            {isMobileOverlayOpen ? (
              <>
                <NoUnderlineButton
                  variant="tertiary"
                  onClick={() => {
                    setFieldValue("store_name", "");
                    setFieldValue("matched_business", {});
                    setIsMobileOverlayOpen(false);
                    setHasSelectedStore(false);
                    setShowDropdown(false);
                  }}
                >
                  Cancel
                </NoUnderlineButton>
                <Spacer width="2x" />
              </>
            ) : null}
          </Row>
          {showDropdown ? (
            <>
              <Spacer
                height={{
                  mobileAndAbove: "1x",
                  tabletAndAbove: 4,
                }}
              />
              <StoreSearchDropdown
                data={data}
                isMobileOverlayOpen={isMobileOverlayOpen}
                query={debounceQuery}
                onSelect={onDropdownSelect}
                hasStoreName={!!values.store_name}
              />
            </>
          ) : null}
        </SearchInputMobileOverlay>
      )}
    </>
  );
};

interface ISelectedInputProps {
  clear: () => void;
}
const SelectedInput: React.FC<ISelectedInputProps> = ({ clear }) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    getFieldProps,
    touched,
    errors,
    values,
    handleChange,
    setFieldValue,
    handleBlur,
  } = useFormikContext<StoreInfoData>();
  const { trackFieldValue } = useSignUpEventTracker(Step.STORE_INFO);
  return values.matched_business?.type === IMatchedBusiness.Type.MANUAL ? (
    <Input
      type="text"
      label={strictLocalize({
        defaultMessage: "Store name",
        description: { text: "Store name input label" },
      })}
      {...getFieldProps("store_name")}
      onChange={(e) => {
        setFieldValue("matched_business", {
          ...values.matched_business,
          name: e.target.value,
        });
        handleChange(e);
      }}
      onBlur={(e) => {
        trackFieldValue(
          "matched_business",
          stringifyMatchBusiness(values.matched_business) ?? ""
        );
        handleBlur(e);
      }}
      error={touched.store_name && errors.store_name}
      suffixNode={
        <CloseIconContainer onClick={clear}>
          <Close width="12px" color={Core.icon.subdued} />
        </CloseIconContainer>
      }
    />
  ) : (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="Store name"
          description={{ text: "Store name input label" }}
        />
      </Typography>
      <SelectedContainer align="center">
        <IconWrapper>
          <Pin width="18px" />
        </IconWrapper>
        <Spacer width={12} />
        <Column flex={1}>
          <Typography maxLines={1}>{values.matched_business?.name}</Typography>
          <Typography
            variant="labelSansRegular"
            color={Core.text.subdued}
            maxLines={1}
          >
            {values.matched_business?.location}
          </Typography>
        </Column>
        <Spacer width={12} />
        <CloseIconContainer onClick={clear}>
          <Close width="12px" color={Core.icon.subdued} />
        </CloseIconContainer>
      </SelectedContainer>
    </Column>
  );
};

const IconWrapper = styled.div<{ $isFocused?: boolean }>`
  display: flex;
  padding: 10px;
  align-items: center;
  border-radius: 32px;
  background-color: ${Core.surface.subdued};
`;

const SelectedContainer = styled(Row)<{ $isFocused?: boolean }>`
  width: 100%;
  align-self: stretch;
  border: 1px solid ${Core.border.muted};
  padding: ${getSpacing(12, "2x")};
`;

const StyledInput = styled(Input)<{
  $isRound?: boolean;
}>`
  ${({ $isRound }) =>
    $isRound &&
    css`
      border-radius: ${getSpacing("4x")};
    `}
`;

const CloseIconContainer = styled.button.attrs({ type: "button" })`
  display: flex;
  width: 12px;
  height: 12px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
`;

const SearchInputMobileOverlay = styled(ClickOutside)<{ $isOpen: boolean }>`
  ${(props) =>
    props.$isOpen
      ? css`
          align-items: flex-start;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 5;
          background-color: ${Core.surface.inverse};
          padding: ${getSpacing("2x")} 0px;
          overflow-y: auto;
        `
      : null}
`;

const SearchWrapper = styled.div`
  pointer-events: none;
  display: flex;
  font-size: 16px;
`;

const NoUnderlineButton = styled(Button)`
  text-decoration: none;
  margin-left: ${getSpacing("2x")};
`;
